import { NavLink } from 'react-router-dom';
import { formatEventDates } from '../lib/dates.js';
import type { SitePayload } from '../lib/site.js';

// The event's masthead and its tabs.
//
// A tab is shown only when its section arrived in the payload. Two reasons, and
// the second is the one that matters: a link to a hidden section is a promise
// the site cannot keep, and a tab that leads to a coming-soon card teaches
// attendees that the tabs are noise.

interface Tab {
  to: string;
  label: string;
  end?: boolean;
}

export function SiteHeader({ payload }: { payload: SitePayload }) {
  const base = `/e/${encodeURIComponent(payload.event.slug)}`;
  const tabs: Tab[] = [{ to: base, label: 'Overview', end: true }];
  if (payload.sessions) tabs.push({ to: `${base}/sessions`, label: 'Schedule' });
  if (payload.speakers) tabs.push({ to: `${base}/speakers`, label: 'Speakers' });

  const dates = formatEventDates(
    payload.event.startAt,
    payload.event.endAt,
    payload.event.timezone,
  );

  return (
    <header className="border-b border-brand-hairline bg-brand-raised">
      <div className="mx-auto w-full max-w-5xl px-4 pt-7 sm:px-6">
        <h1 className="e67-title text-2xl text-balance sm:text-3xl">{payload.event.name}</h1>
        <p className="mt-1.5 text-[13px] text-brand-muted">
          {[dates, payload.event.location].filter(Boolean).join(' · ')}
        </p>

        {tabs.length > 1 && (
          <nav aria-label="Event sections" className="mt-5">
            <ul className="-mb-px flex gap-1 overflow-x-auto">
              {tabs.map((tab) => (
                <li key={tab.to}>
                  <NavLink
                    to={tab.to}
                    end={tab.end}
                    className={({ isActive }) =>
                      `inline-block whitespace-nowrap border-b-2 px-3.5 py-3 text-sm font-semibold transition ${
                        isActive
                          ? 'border-brand-primary text-brand-on-surface'
                          : 'border-transparent text-brand-muted hover:text-brand-on-surface'
                      }`
                    }
                  >
                    {tab.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
        {tabs.length === 1 && <div className="h-5" />}
      </div>
    </header>
  );
}
